import ky from 'ky';

import useSWR, { SWRConfiguration } from 'swr';

import { BASE_URL, URLS } from 'config/urls';
import { LocalStorageKeyEnum } from './constants';
import { invokeListeners } from './sdkToastBus.ts';
import { NotificationStatusEnum } from 'components/Toast/constants';
import i18n from 'i18next';

export type IConfig = RequestInit & {
  isFileUpload?: boolean;
  timeout?: false | number;
};

type IHeaders = Record<string, string>;

const api = ky.create({
  prefixUrl: BASE_URL,
  hooks: {
    beforeRequest: [
      (request) => {
        const token = localStorage.getItem(LocalStorageKeyEnum.AUTH_TOKEN);

        if (token) {
          request.headers.set('Authorization', `Bearer ${token}`);
        }
      },
    ],
    afterResponse: [
      async (_request, _options, response) => {
        if (response.status === 401) {
          localStorage.removeItem(LocalStorageKeyEnum.AUTH_TOKEN);

          if (window.location.pathname !== URLS.LOGIN && window.location.pathname !== URLS.SET_PASSWORD) {
            window.location.replace(URLS.LOGIN);
          }
        }
      },
      async (_request, _options, response) => {
        let toastTitle: string | null = null;
        if (response.status >= 500) {
          toastTitle = i18n.t('notification.somethingWentWrong');
        } else if (response.status === 401) {
          toastTitle = i18n.t('notification.unauthorized');
        } else if (response.status === 403) {
          toastTitle = i18n.t('notification.forbidden');
        } else if (response.status === 404) {
          toastTitle = i18n.t('notification.notFound');
        }

        if (toastTitle !== null) {
          invokeListeners({ title: toastTitle, status: NotificationStatusEnum.ERROR, timeout: 5000 });
        }
      },
    ],
  },
});

export const getConfig = (params?: IConfig) => {
  const { headers = {}, isFileUpload = false, ...options } = params || {};

  const contentType = isFileUpload ? undefined : 'application/json';

  const config: IConfig = {
    headers: {
      'Content-Type': contentType,
      ...headers,
    } as IHeaders,
    credentials: 'include',
    timeout: false,
    ...options,
  };

  return config;
};

export const get = <R>(url: string): Promise<R> => api.get(url, getConfig()).json<R>();

export const post = <R>(url: string, data: unknown): Promise<R> =>
  api.post(url, { ...getConfig(), json: data }).json<R>();

export const useFetch = <D>(url: string | null, config?: SWRConfiguration) =>
  useSWR<D>(url, get, {
    revalidateIfStale: true,
    revalidateOnFocus: true,
    revalidateOnReconnect: true,
    ...config,
  });
